import {
  AbsoluteCenter,
  Box,
  Container,
  Flex,
  useColorModeValue
} from '@chakra-ui/react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';

import useIsLandScapeMachine from '@/hook/useIsLandScapeMachine';
import useLongMachineSetting from '@/hook/useLongMachineSetting';

import { COMMON_WEB_PATH, WEB_LAYOUT } from '../../constant';
import useCheckLogin from '../../hook/useCheckLogin';
import useDeviceType from '../../hook/useDeviceType';
import useIsLandScape from '../../hook/useIsLandScape';
import {
  defaultGameStatus,
  playedGameStatus,
  playingGameStatus
} from '../../redux/action/member/memberAction';
import FloatButton from '../floatButton/floatButton';
import GameSearch from './HeaderComponents/GameSearch';
import LongMachineVideo from '../LongMachineVideo/LongMachineVideo';
import MachineFooter from '../MachineFooter/MachineFooter';
import Fireworks from '../NewYearDecorations/components/Fireworks';
import Footer from './Footer';
import Header from './Header';
import HeaderBottom from './HeaderBottom';

const isCredit = process.env.REACT_APP_CREDIT == 1;

const Wrapper = ({ children, scrollToTop }) => {
  const { t } = useTranslation();
  const i18n_ranking = (key) => t(`ranking.${key}`);

  const isStickyLeft = useSelector((state) => state.isStickyLeft);
  const gameBoxStatus = useSelector((state) => state.isGameBoxStatus);
  const gameConfig = useSelector((state) => state.isGameConfig);

  const dispatch = useDispatch();

  const isVerticalGame = gameConfig?.support_vertical === 1;

  // 取 登入狀態 機台相關的狀態
  const { isLogin, isMachineDomain, isMachineUser } = useCheckLogin();

  const deviceType = useDeviceType();
  const scrollContainerRef = useRef(null);

  const menuBg = useColorModeValue('light.100', 'dark.100');
  const menuBorder = useColorModeValue('gray.300', 'dark.100');
  const boxBg = useColorModeValue(
    'linear(to-bl,#F3F6FE,#F3F6FE,#FCE5FE)',
    'linear(to-bl,#1A2036,#1A2036,#1A2036)'
  );
  const mainBg = useColorModeValue('transparent', '#262D47');

  const location = useLocation();

  const navigate = useNavigate();

  const [showScrollToTop, setShowScrollToTop] = useState(false);
  const isMobile = deviceType === 'Mobile';
  const isMobileAndLandScape = useIsLandScape({ isMobile: true });

  useEffect(() => {
    if (scrollContainerRef.current && scrollToTop) {
      setTimeout(() => {
        scrollContainerRef.current?.scrollIntoView({
          block: 'start',
          behavior: 'smooth'
        });
        scrollContainerRef.current?.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      }, 100);
    }
  }, [location.pathname]);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      if (currentScrollY > 1000) {
        setShowScrollToTop(true);
      } else {
        setShowScrollToTop(false);
      }
    };

    window.addEventListener('scroll', handleScroll);

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isGameBox = useMemo(() => {
    return location.pathname.includes('gamebox');
  }, [location.pathname]);

  const { pathname } = location;
  useEffect(() => {
    if (pathname.includes('gamebox')) {
      dispatch(playingGameStatus());
      return;
    }

    if (gameBoxStatus === 'playing') {
      dispatch(playedGameStatus());
      dispatch({ type: 'GET_BALANCE' });
      setTimeout(() => {
        dispatch(defaultGameStatus());
      }, 1500);
    }
  }, [pathname, gameBoxStatus]);

  const headerVersion = useMemo(() => {
    switch (WEB_LAYOUT) {
      case 'v2':
        return {
          wrapperTopPadding: {
            // base: "55px",
            // sm: "55px",
            base: '0px',
            sm: '0px'
          }
        };
      default:
        return {
          wrapperTopPadding: {
            // base: "50px",
            // sm: "56px",
            base: '0px',
            sm: '0px'
          }
        };
    }
  }, [WEB_LAYOUT]);

  const { header, footer, wrapperTopPadding } = headerVersion;

  const isLandscapeMachine = useIsLandScapeMachine();
  const { longMachineVideoHeight } = useLongMachineSetting();

  return (
    <Box>
      <Container
        px={{
          base: '0px'
        }}
        maxW="100%"
        pt={
          isLandscapeMachine
            ? isGameBox && isVerticalGame
              ? ''
              : longMachineVideoHeight
            : ''
        }
      >
        {isGameBox && isMobile ? null : (
          <>
            {isLandscapeMachine ? (
              <>
                {isGameBox && isVerticalGame ? null : <LongMachineVideo />}
                <HeaderBottom />
              </>
            ) : (
              <>
                <Fireworks sx={{ right: '-6rem', top: '-3rem' }} />
                <Header />
                <Box
                  height={{
                    base: isGameBox ? '0px' : '60px',
                    sm: '60px',
                    md: '100px',
                    xl: isMachineUser ? '125px' : '125px'
                  }}
                />
              </>
            )}
          </>
        )}

        <Flex
          pt={{
            base: isMobileAndLandScape && isGameBox ? '0px' : '5px',
            lg: isMobileAndLandScape && isGameBox ? '0px' : '0px'
          }}
          overflowY={isLandscapeMachine ? 'scroll' : ''}
          maxH={{
            md: isLandscapeMachine
              ? isGameBox
                ? '100dvh'
                : `calc(100dvh - ${longMachineVideoHeight})`
              : ''
          }}
        >
          <Box
            maxW={{
              base: '100%',
              xl: isGameBox ? '100%' : 'calc(100% - 0px)'
            }}
            flex={1}
            w={`100%`}
            ml={{
              base: '0px',
              xl: '0px'
            }}
            mr={{
              base: '0px',
              md: ``
            }}
            h={{
              base: 'auto',
              md: isGameBox ? '100dvh' : '100%'
            }}
            bg={{
              base: 'transparent',
              sm: 'transparent'
            }}
            borderRadius={{
              base: 'unset',
              sm: '24px'
            }}
            px={{
              base: '10px',
              md: '0px'
            }}
            pl={{
              md: '0px'
            }}
            mx={{
              base: 'unset',
              sm: '10px',
              md: 0
            }}
            className={`${isMobileAndLandScape && isGameBox ? '!mx-0' : ''}`}
          >
            <Flex justifyContent={`space-between`} h={`100%`}>
              <Box
                h={`100%`}
                display={`flex`}
                w="100%"
                maxW={
                  isGameBox
                    ? {
                        base: '100%',
                        xl: '100%',
                        '2xl': '100%'
                      }
                    : {
                        base: '100%',
                        md: '100%',
                        xl: '100%',
                        '2xl': isCredit ? '100%' : 'calc(100%)'
                        // md: "1188px",
                      }
                }
                mx={
                  isGameBox
                    ? {
                        base: 'auto'
                      }
                    : {
                        base: 'auto',
                        xl: '0px'
                      }
                }
              >
                <Box
                  ref={scrollContainerRef}
                  minW={{
                    base: '0px',
                    lg: '772px',
                    xl: 'unset'
                  }}
                  w="100%"
                  overflowX={{
                    base: '',
                    lg: 'unset'
                  }}
                  className={`${
                    isStickyLeft && '!max-w-[1440px]'
                  } no-scrollbar`}
                  pt={{
                    base: isGameBox ? '0px' : wrapperTopPadding.base,
                    sm: wrapperTopPadding?.sm
                  }}
                >
                  {children}
                </Box>
              </Box>
            </Flex>

            {isGameBox ? null : deviceType === 'PC' ||
              deviceType === 'Tablet' ||
              isLandscapeMachine ? (
              <MachineFooter />
            ) : (
              isMobile && <Footer />
            )}
            <FloatButton />
            {/* 遊戲搜尋:固定浮動按鈕(gamebox 內不顯示)*/}
            {isGameBox ? null : <GameSearch />}
          </Box>
        </Flex>
      </Container>
    </Box>
  );
};

export default Wrapper;
